import { v4 as uuidv4 } from 'uuid'
import { ACCOUNT_TYPES, LOCATION_TYPES, ID_TYPES } from 'utils/constants'
import { sanitizedValues } from 'utils/helpers'
import Dinero from 'dinero.js'
import store from 'state/store'

const getPrimaryContact = (locationId, contacts) => {
  const primaryContact = contacts?.find(contact => contact.locationId === locationId && contact.isPrimaryContact)
  return primaryContact ? primaryContact.id : null
}

export const isPrimaryContact = (contacts, locations) => {
  if (!locations) return false
  const { id, locationId } = contacts
  const location = locations?.find(item => item.id === locationId && item.primaryContactId === id)
  return !!location
}

export const isDirtyFieldInContacts = (dirtyFields, field) => {
  const { contacts } = dirtyFields
  const isDirty = contacts?.find(contact => {
    if (contact) return field in contact
    return false
  })
  return !!isDirty
}

export const getComplianceData = contact => ({
  firstNameLegal: contact.firstNameLegal || '',
  lastNameLegal: contact.lastNameLegal || '',
  address1: contact.address1 || '',
  address2: contact.address2 || '',
  city: contact.city || '',
  zip: contact.zip || '',
  state: contact.state || '',
  idType: contact.idType || '',
  licenseNumber: contact.licenseNumber || '',
  licenseExpiration: contact.licenseExpiration?.getTime
    ? contact.licenseExpiration.getTime()
    : new Date(Number(contact.licenseExpiration) || contact.licenseExpiration).getTime() || null,
  height: contact.height || '',
  eyes: contact.eyes || '',
  hair: contact.hair || '',
  gender: contact.gender || '',
  birthDate: contact.birthDate?.getTime
    ? contact.birthDate.getTime()
    : new Date(Number(contact.birthDate) || contact.birthDate).getTime() || null,
  licenseImage: contact.licenseImage || '',
  licenseImageBack: contact.licenseImageBack || '',
  race: contact.race ? contact.race : null,
  weight: contact.weight ? parseInt(contact.weight) : null,
})

export const formatContact = contact => ({
  ...contact,
  ...getComplianceData(contact),
  firstName: contact.firstName || '',
  lastName: contact.lastName || '',
  image: contact.image || '',
  thumbprintImage: contact.thumbprintImage || '',
  signatureImage: contact.signatureImage || '',
  texasCashCardAppDocspringId: contact.texasCashCardAppDocspringId || '',
  texasCashCardDocspringId: contact.texasCashCardDocspringId || '',
  texasCashCardId: contact.texasCashCardId || '',
  texasCashCardExpirationDate: contact.texasCashCardExpirationDate || '',
  locationId: contact.locationId || null,
  employmentStatus: contact.employmentStatus || null,
})

export const formatData = data => {
  const { enableAddressPrintingUpdates = false } = store.getState().featureFlags?.variables || {}
  const values = {
    ...data,
    notes: data.notes ? data.notes : null,
    buysForbidden: data.canBuy !== undefined ? !data.canBuy : false,
    accountManagerGsUserId: data.accountManagerGsUserId,
    isConsumer: data.accountType
      ? data.accountType === ACCOUNT_TYPES.SUPPLIERANDCONSUMER.id || data.accountType === ACCOUNT_TYPES.CONSUMER.id
      : data.isConsumer,
    isSupplier: data.accountType
      ? data.accountType === ACCOUNT_TYPES.SUPPLIERANDCONSUMER.id || data.accountType === ACCOUNT_TYPES.SUPPLIER.id
      : data.isSupplier,
    isCarrier: data.accountType ? data.accountType === ACCOUNT_TYPES.CARRIER.id : data.isCarrier,
    accountType: data.accountType || '',
    canBuy: data.canBuy || '',
    creditBalance:
      data.creditBalance || data.creditBalance === 0
        ? Dinero({
            amount: parseInt(data.creditBalance, 10),
            precision: 0,
            currency: data.defaultCurrency,
          }).toObject()
        : null,
    docs: data.docs || [],
    uploadcareGroupId: data.uploadcareGroupId || null,
    preferredPaymentMethod: data.preferredPaymentMethod?.toUpperCase() || null,
    taxNumber: data.taxNumber || null,
    taxRate: data.taxRate || null,
    accountingNumber: data.accountingNumber || null,
    driverIds: data.driverIds || [],
  }

  const customer = sanitizedValues(values)
  const contacts = !customer.contacts // This is a hotfix to handle update of suppliers should be refactored
    ? [
        {
          id: uuidv4(),
          firstName: 'Default',
          lastName: 'Contact',
          idType: 'license',
        },
      ]
    : customer.contacts.map(contact => {
        const formattedContact = formatContact(contact)
        formattedContact.isDefaultPayToContact = customer.defaultPayToContact === contact.id
        delete formattedContact.primaryContact
        return formattedContact
      })
  const locations =
    customer.locations?.map(location => {
      let { isCorporate, isShipping, isPayToLocation } = location

      // only update isCorporate, isShipping, and isPayToLocation if form data includes `locationTypes` or `locationType`
      if ('locationTypes' in location || 'locationType' in location) {
        isCorporate = enableAddressPrintingUpdates
          ? location.locationTypes?.indexOf(LOCATION_TYPES.CORPORATE.id) > -1
          : location.locationType === LOCATION_TYPES.CORPORATE.id
        isShipping = enableAddressPrintingUpdates
          ? location.locationTypes?.indexOf(LOCATION_TYPES.SHIPPING.id) > -1
          : location.locationType === LOCATION_TYPES.SHIPPING.id
        isPayToLocation = enableAddressPrintingUpdates
          ? location.locationTypes?.indexOf(LOCATION_TYPES.PAY_TO.id) > -1
          : location.locationType === LOCATION_TYPES.PAY_TO.id
      }

      const locationValues = {
        ...location,
        isCorporate,
        isShipping,
        isPayToLocation,
        primaryContactId: getPrimaryContact(location.id, customer.contacts),
        isDefaultCorporateLocation: isCorporate && customer.defaultCorporateLocation === location.id,
        isDefaultShippingLocation: isShipping && customer.defaultShippingLocation === location.id,
        isDefaultPayToLocation: isPayToLocation && customer.defaultPayToLocation === location.id,
        // TODO: Add freightPricing to the schema
        freightPricing: {
          id: location.freightPricing?.id || undefined,
          consumerFreightEnabled: location.freightPricing?.consumerFreightEnabled || false,
          supplierFreightEnabled: location.freightPricing?.supplierFreightEnabled || false,
          consumerFreightChargeback: location.freightPricing?.consumerNegativeCharge
            ? -Math.abs(location.freightPricing?.consumerFreightChargeback || 0)
            : Math.abs(location.freightPricing?.consumerFreightChargeback || 0),
          supplierFreightChargeback: location.freightPricing?.supplierFreightChargeback || 0,
          consumerFreightCost: location.freightPricing?.consumerFreightCost || 0,
          supplierFreightCost: location.freightPricing?.supplierFreightCost || 0,
          locationId: location.freightPricing?.locationId || undefined,
        },
      }
      delete locationValues.locationType // REMOVE this line when "enableAddressPrintingUpdates" FF is turned on
      delete locationValues.locationTypes // KEEP this line when "enableAddressPrintingUpdates" FF is turned on
      return locationValues
    }) || []
  const vehicles =
    customer.vehicles
      ?.filter(vehicle => !Object.values(vehicle).every(value => !value))
      .map(v => ({
        ...v,
        id: v.id || uuidv4(),
      })) || []
  const customerCommonIdentifierString =
    customer.companyName || `${contacts?.[0]?.firstName} ${contacts?.[0]?.lastName}`
  const companyName = customer.companyName || `${contacts?.[0]?.firstName} ${contacts?.[0]?.lastName}`
  return {
    ...customer,
    customerCommonIdentifierString,
    companyName,
    contacts,
    locations,
    vehicles,
  }
}

export const idTypesArray = [ID_TYPES.LICENSE]

export const accountTypesArray = [
  ACCOUNT_TYPES.SUPPLIER,
  ACCOUNT_TYPES.CONSUMER,
  ACCOUNT_TYPES.CARRIER,
  ACCOUNT_TYPES.SUPPLIERANDCONSUMER,
]

export const getDefaultValues = () => {
  const { enableAddressPrintingUpdates = false, enableCountryCodes } = store.getState().featureFlags?.variables || {}
  const { country: yardCountry } = store.getState().settings.details

  return {
    canBuy: true,
    isSupplier: false,
    isConsumer: false,
    isCarrier: false,
    accountType: '',
    vendorClassId: '',
    companyName: '',
    notes: '',
    docs: [],
    uploadcareGroupId: '',
    supplierTypeId: '',
    priceSheetId: '',
    accountManagerGsUserId: '',
    status: null,
    gsPayContactId: '',
    preferredPaymentMethod: '',
    defaultPaymentTermId: '',
    creditBalance: '',
    defaultCurrency: '',
    taxNumber: '',
    taxRate: '',
    accountingNumber: '',
    locations: [
      {
        id: uuidv4(),
        name: '',
        ...(enableAddressPrintingUpdates ? { locationTypes: [] } : { locationType: '' }),
        address1: '',
        address2: '',
        city: '',
        state: '',
        zip: '',
        country: enableCountryCodes ? yardCountry : undefined,
        notes: '',
        primaryContactId: '',
        freightPricing: {
          consumerFreightEnabled: false,
          supplierFreightEnabled: false,
          consumerFreightChargeback: 0,
          supplierFreightChargeback: 0,
          consumerFreightCost: 0,
          supplierFreightCost: 0,
          locationId: undefined,
        },
      },
    ],
    contacts: [
      {
        id: uuidv4(),
        firstName: '',
        lastName: '',
        email: '',
        role: '',
        phone1: '',
        phone2: '',
        locationId: '',
        notes: '',
        firstNameLegal: '',
        lastNameLegal: '',
        address1: '',
        address2: '',
        city: '',
        zip: '',
        state: '',
        idType: idTypesArray[0].id,
        licenseNumber: '',
        licenseExpiration: null,
        height: '',
        eyes: '',
        hair: '',
        gender: '',
        birthDate: null,
        licenseImage: '',
        licenseImageBack: '',
        isPrimaryContact: false,
        placeOfEmployment: '',
        employmentStatus: null,
      },
    ],
    vehicles: [],
    defaultCorporateLocation: '',
    defaultPayToLocation: '',
    defaultShippingLocation: '',
    defaultPayToContact: '',
    driverIds: [],
  }
}

// This method is required because customer shouldn't contain undefined values to allow RHF to validate dirty values
export const formatCustomer = (initialValues, customer) => {
  const { enableAddressPrintingUpdates = false } = store.getState().featureFlags?.variables || {}

  const isNeither = !(!!customer.isSupplier || !!customer.isConsumer)
  const {
    locations,
    contacts,
    buysForbidden,
    preferredPaymentMethod,
    creditBalance,
    supplierTypeId,
    vendorClassId,
    priceSheetId,
    accountManagerGsUser,
  } = customer

  const formattedDefaults = {}

  const formattedLocations = []
  locations?.forEach(location => {
    // REMOVE the below when "enableAddressPrintingUpdates" FF is turned on
    let locationType
    if (location.isCorporate) {
      locationType = LOCATION_TYPES.CORPORATE.id
    } else if (location.isShipping) {
      locationType = LOCATION_TYPES.SHIPPING.id
    } else if (location.isPayToLocation) {
      locationType = LOCATION_TYPES.PAY_TO.id
    }

    // KEEP the below when "enableAddressPrintingUpdates" FF is turned on
    const locationTypes = []
    if (location.isCorporate) {
      locationTypes.push(LOCATION_TYPES.CORPORATE.id)
    }
    if (location.isShipping) {
      locationTypes.push(LOCATION_TYPES.SHIPPING.id)
    }
    if (location.isPayToLocation) {
      locationTypes.push(LOCATION_TYPES.PAY_TO.id)
    }
    if (location.isDefaultCorporateLocation) {
      formattedDefaults.defaultCorporateLocation = location.id
    }
    if (location.isDefaultShippingLocation) {
      formattedDefaults.defaultShippingLocation = location.id
    }
    if (location.isDefaultPayToLocation) {
      formattedDefaults.defaultPayToLocation = location.id
    }

    formattedLocations.push({
      ...initialValues.locations[0],
      ...location,
      freightPricing: {
        ...location.freightPricing,
        consumerFreightChargeback: location.freightPricing?.consumerFreightChargeback || 0,
        supplierFreightChargeback: location.freightPricing?.supplierFreightChargeback || 0,
        consumerNegativeCharge: location.freightPricing?.consumerFreightChargeback < 0,
        consumerFreightCost: location.freightPricing?.consumerFreightCost || 0,
        supplierFreightCost: location.freightPricing?.supplierFreightCost || 0,
      },
      ...(enableAddressPrintingUpdates ? { locationTypes } : { locationType }),
    })
  })

  const formattedContacts = []
  contacts?.forEach(contact => {
    if (contact.isDefaultPayToContact) {
      formattedDefaults.defaultPayToContact = contact.id
    }
    formattedContacts.push(
      formatContact({
        ...initialValues.contacts[0],
        ...contact,
      }),
    )
  })

  return {
    ...initialValues,
    ...customer,
    companyName: isNeither ? customer.customerCommonIdentifierString : customer.companyName || customer.displayName,
    supplierTypeId: supplierTypeId || '',
    vendorClassId: vendorClassId || '',
    priceSheetId: priceSheetId || '',
    accountManagerGsUserId: accountManagerGsUser?.accountManagerGsUserId || '',
    canBuy: !buysForbidden,
    accountType:
      (customer.isSupplier && customer.isConsumer && ACCOUNT_TYPES.SUPPLIERANDCONSUMER.id) ||
      (customer.isSupplier && ACCOUNT_TYPES.SUPPLIER.id) ||
      (customer.isConsumer && ACCOUNT_TYPES.CONSUMER.id) ||
      (customer.isCarrier && ACCOUNT_TYPES.CARRIER.id) ||
      '',
    locations: formattedLocations,
    contacts: formattedContacts,
    ...formattedDefaults,
    preferredPaymentMethod: preferredPaymentMethod?.toLowerCase() || '',
    creditBalance: Dinero({ ...creditBalance }).toUnit() || '',
    driverIds: customer?.drivers?.map(driver => driver.id) || [],
  }
}
